import { z } from 'zod';
import { MONEY_MAX_BILLS_PER_CHANGE, MONEY_STEP, RESOURCE_KEYS } from '../domain/game.js';
import { DEMO_SCENARIOS } from '../domain/demo.js';

export const createGameSessionSchema = z.object({
  title: z.string().trim().min(2).max(120).nullable().optional(),
  /**
   * «Играть самостоятельно» — ведущий сразу садится за собственный стол.
   *
   * Не отдельный режим сессии, а всего лишь занятое место: партия остаётся
   * обычной, и позвать в неё людей по ссылке можно и после.
   */
  playSelf: z.boolean().optional(),
  /**
   * Сценарий демопартии. Обязателен для демо и запрещён для полной игры —
   * это проверяет сервер по записи игры, а не по адресу.
   */
  demoScenario: z.enum(DEMO_SCENARIOS).optional(),
});
export type CreateGameSessionInput = z.infer<typeof createGameSessionSchema>;

export const gameParticipantCommandSchema = z.object({
  participantId: z.string().min(1),
});
export type GameParticipantCommandInput = z.infer<typeof gameParticipantCommandSchema>;

export const adjustResourceSchema = z.object({
  participantId: z.string().min(1),
  resource: z.enum(RESOURCE_KEYS),
  delta: z.union([z.literal(-1), z.literal(1)]),
});
export type AdjustResourceCommandInput = z.infer<typeof adjustResourceSchema>;

export const adjustMoneySchema = z.object({
  participantId: z.string().min(1),
  delta: z
    .number()
    .int()
    .refine((value) => value !== 0, { message: 'Сумма не может быть нулевой' })
    .refine((value) => value % MONEY_STEP === 0, {
      message: `Сумма должна быть кратна ${MONEY_STEP}`,
    })
    .refine((value) => Math.abs(value) <= MONEY_STEP * MONEY_MAX_BILLS_PER_CHANGE, {
      message: `За один раз можно изменить не больше ${MONEY_MAX_BILLS_PER_CHANGE} купюр`,
    }),
});
export type AdjustMoneyInput = z.infer<typeof adjustMoneySchema>;

export const gameCardCommandSchema = z.object({
  participantId: z.string().min(1),
});
export type GameCardCommandInput = z.infer<typeof gameCardCommandSchema>;

/** GAME-32 — какую сказку сектора открывает ведущий. */
export const openStorySchema = z.object({
  sectorId: z.string().min(1),
});
export type OpenStoryInput = z.infer<typeof openStorySchema>;

/** GAME-35 — подарок ресурса от одного участника другому. */
export const transferResourceSchema = z.object({
  fromParticipantId: z.string().min(1),
  toParticipantId: z.string().min(1),
  resource: z.enum(RESOURCE_KEYS),
});
export type TransferResourceInput = z.infer<typeof transferResourceSchema>;

/**
 * GAME-36 — обмен ресурса на карту Таланта в «Восточном базаре».
 *
 * Меняются только ресурсные единицы. Числовой денежный баланс сюда не входит:
 * это открытый вопрос к Дарье, и подставлять за неё правило нельзя.
 */
export const bazaarExchangeSchema = z.object({
  participantId: z.string().min(1),
  resource: z.enum(RESOURCE_KEYS),
});
export type BazaarExchangeInput = z.infer<typeof bazaarExchangeSchema>;

/** GAME-39 — чем ведущий закрывает карту события после ответа участника. */
export const eventCardAwardSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('resource'), resource: z.enum(RESOURCE_KEYS) }),
  z.object({ kind: z.literal('talent') }),
  z.object({ kind: z.literal('none') }),
]);
export type EventCardAward = z.infer<typeof eventCardAwardSchema>;

export const resolveEventCardSchema = z.object({
  sessionCardId: z.string().min(1),
  award: eventCardAwardSchema,
});
export type ResolveEventCardInput = z.infer<typeof resolveEventCardSchema>;

/** Публикация игры в каталоге — демо создаётся черновиком и публикуется вручную. */
export const setGameStatusSchema = z.object({
  status: z.enum(['draft', 'published']),
});
export type SetGameStatusInput = z.infer<typeof setGameStatusSchema>;
